// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useVendorByNameQuery, type VendorRow } from "@/api/hooks/use-vendors";
import { PageHeader } from "@/components/layout/page-header";
import { ChainLink } from "@/components/shared/chain-link";
import { DateTimeValue } from "@/components/shared/datetime-value";
import { IdPill } from "@/components/shared/id-pill";
import { KeyChip } from "@/components/shared/key-chip";
import { DetailPageSkeleton, PageProgressBar } from "@/components/shared/page-loading";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleVendorActiveDialog } from "@/features/iam/toggle-vendor-active-dialog";
import { VendorActionsMenu } from "@/features/iam/vendor-actions-menu";
import { VendorActivityTab } from "@/features/iam/vendor-activity-tab";
import { VendorUsageTab } from "@/features/iam/vendor-usage-tab";
import { useDebugMode } from "@/lib/debug-mode";

interface VendorDetailProps {
  vendorName: string;
  tab?: string;
  initialFilters?: string;
}

export function VendorDetail({ vendorName, tab, initialFilters }: VendorDetailProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { debugMode } = useDebugMode();

  const { data: vendor, isLoading, isError } = useVendorByNameQuery(vendorName);

  // Fall back to Info when a hidden tab is requested.
  const requestedTab = tab ?? "info";
  const tabAllowed: Record<string, boolean> = {
    info: true,
    usage: true,
    // Activity log is work-in-progress — debug-mode only.
    activity: debugMode,
  };
  const activeTab = tabAllowed[requestedTab] ? requestedTab : "info";

  const [toggleVendor, setToggleVendor] = useState<VendorRow | null>(null);

  const breadcrumbs = useMemo(() => [{ label: t("vendors.title"), to: "/iam/vendors" }], [t]);

  function handleTabChange(newTab: string) {
    navigate({
      to: "/iam/vendors/$vendorName",
      params: { vendorName },
      search: newTab === "info" ? {} : { t: newTab },
      replace: true,
    });
  }

  if (isLoading) {
    return (
      <div>
        <PageProgressBar />
        <DetailPageSkeleton />
      </div>
    );
  }

  if (isError || !vendor) {
    return (
      <div>
        <PageHeader title={vendorName} breadcrumbs={breadcrumbs} />
        <div className="p-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {t("errors.loadFailed")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={vendor.name ?? `#${vendor.vendorId}`}
        subtitle={
          <>
            <IdPill id={vendor.vendorId} />
            <StatusIndicator active={vendor.isActive} />
            <VendorActionsMenu
              isActive={vendor.isActive}
              onToggleActive={() => setToggleVendor(vendor)}
              triggerClassName="ml-auto -my-1.5"
            />
          </>
        }
        breadcrumbs={breadcrumbs}
      />
      <div>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
          <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
            <TabsList>
              <TabsIndicator />
              <TabsTrigger value="info">{t("tabs.info")}</TabsTrigger>
              <TabsTrigger value="usage">{t("tabs.usage")}</TabsTrigger>
              {debugMode && <TabsTrigger value="activity">{t("tabs.activityLog")}</TabsTrigger>}
            </TabsList>
          </div>
          <TabsContent value="info" className="space-y-4">
            <div className="max-w-2xl space-y-6 p-6">
              <div className="grid grid-cols-[160px_1fr] gap-y-3 text-sm">
                <span className="text-muted-foreground">{t("vendors.list.columns.name")}</span>
                <span>{vendor.name ?? <span className="text-muted-foreground">—</span>}</span>

                <span className="text-muted-foreground">{t("vendors.list.columns.apiKey")}</span>
                <span>
                  {vendor.apiKey ? <KeyChip label={vendor.apiKey} /> : <span className="text-muted-foreground">—</span>}
                </span>

                <span className="text-muted-foreground">{t("vendors.list.columns.chain")}</span>
                <span>
                  {vendor.chainName ? (
                    <ChainLink chainName={vendor.chainName} legibleName={vendor.chainName} />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </span>

                <span className="text-muted-foreground">{t("vendors.list.columns.status")}</span>
                <span>
                  <StatusIndicator active={vendor.isActive} />
                </span>

                <Separator className="col-span-2" />

                <span className="text-muted-foreground">{t("common.createdAt")}</span>
                <DateTimeValue value={vendor.createdAt} />
              </div>
            </div>
          </TabsContent>
          <TabsContent value="usage" className="space-y-4">
            <VendorUsageTab vendorName={vendor.name ?? vendorName} />
          </TabsContent>
          {debugMode && (
            <TabsContent value="activity" className="space-y-4">
              <VendorActivityTab
                vendorName={vendor.name ?? vendorName}
                initialFilters={activeTab === "activity" ? initialFilters : undefined}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>
      <ToggleVendorActiveDialog vendor={toggleVendor} onClose={() => setToggleVendor(null)} />
    </div>
  );
}
